/**
 * The velocity alert detector's current answer, above the Velocity page:
 * any open incident, and each watched stream's state. The same evaluation
 * the Slack bot posts from (backend services/velocity/liveAlerts), so what
 * this strip says is what the channel was told.
 */

import { useEffect, useState, useCallback } from 'react'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { velocityAPI } from '../../services/api'
import { Card, CardContent } from '../ui'

const REFRESH_MS = 5 * 60 * 1000
const clock = iso => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
const dayClock = iso => new Date(iso).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })

export default function VelocityAlerts() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    velocityAPI.getAlerts()
      .then(r => { setData(r.data.data); setError(null) })
      .catch(err => setError(err.response?.data?.error?.message || err.message))
  }, [])
  useEffect(() => {
    load()
    const t = setInterval(load, REFRESH_MS)
    return () => clearInterval(t)
  }, [load])

  if (error) return <p className="text-xs text-muted-foreground">Alerts unavailable: {error}</p>
  if (!data) return <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Checking for stops…</div>

  const open = data.incidents.filter(i => i.open && i.status === 'ALERT')
  const recent = data.incidents.filter(i => !i.open && i.status === 'ALERT')
  const suppressed = data.incidents.filter(i => i.status === 'suppressed:budget')

  return (
    <Card className={open.length ? 'border-red-300 dark:border-red-800' : ''}>
      <CardContent className="py-3 px-4 space-y-2">
        {open.map(i => (
          <div key={i.stream + i.startedAt} className="flex items-start gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
            <div>
              <span className="font-semibold">{i.label} looks stopped</span> since about {clock(i.startedAt)} ET:{' '}
              {i.observed.toLocaleString()} seen vs about {i.expected.toLocaleString()} expected ({Math.min(99, Math.floor(i.probability * 100))}% likely an outage).
            </div>
          </div>
        ))}
        {!open.length && (
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
            <span>No stops detected.</span>
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {data.streams.map(s => (
            <span key={s.stream} title={s.through ? `Judged through ${clock(s.through)} ET` : 'No data judged yet'}>
              {s.label}{' '}
              <span className={s.probability >= 0.5 ? 'text-red-600 font-medium' : 'text-foreground'}>
                {s.recent && s.recent.expected > 0 ? `${Math.round((100 * s.recent.observed) / s.recent.expected)}% of normal` : '–'}
              </span>
            </span>
          ))}
        </div>
        {(recent.length > 0 || suppressed.length > 0) && (
          <div className="text-xs text-muted-foreground">
            {recent.map(i => <div key={i.stream + i.startedAt}>Resolved: {i.label} stopped {dayClock(i.startedAt)} → {clock(i.endedAt)} ET</div>)}
            {suppressed.map(i => <div key={i.stream + i.startedAt}>Not alerted: {i.label} stopped {dayClock(i.startedAt)} after a full day&apos;s spend (budget)</div>)}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
